<template>
  <v-card flat class="custom-grey-border remove-border-radius mt-4">
    <!-- HEADER -->
    <v-card-title class="headline grey lighten-4">
      <v-row>
        <v-col md="6" class="py-0 my-auto">
          <h3
            class="font-weight-700 custom-headline color-custom-blue"
            style="margin: 3.5px 0"
          >
            Quotation Category & Sub-category
          </h3>
        </v-col>
        <v-col
          md="6"
          class="text-right py-0"
          v-if="getPermission('setting:update')"
        >
          <v-btn
            depressed
            small
            color="cyan"
            class="white--text"
            @click="openAddCategoryDialog"
          >
            <v-icon small left>mdi-plus-circle</v-icon>
            <span class="font-size-16 font-weight-600">Add Category</span>
          </v-btn>
        </v-col>
      </v-row>
    </v-card-title>

    <!-- CATEGORY TABLE -->
    <v-card-text class="p-6 font-size-16">
      <v-simple-table class="simple-table elevation-1">
        <thead>
          <tr>
            <th class="simple-table-th px-1" width="50">#</th>
            <th class="simple-table-th px-1" width="500">Category Name</th>
            <th class="simple-table-th px-1" width="150">Sub-categories</th>
            <th class="simple-table-th px-1 text-end" width="150">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="categories?.length > 0">
            <tr
              v-for="(category, index) in paginatedCategories"
              :key="'cat-list-' + category.id"
              class="alternate-listing-row"
            >
              <td width="50" class="simple-table-td">
                <v-chip small>{{
                  (currentPage - 1) * itemsPerPage + index + 1
                }}</v-chip>
              </td>
              <td width="500" class="simple-table-td">
                <div class="d-flex align-center">
                  <v-icon color="cyan" class="mr-2">mdi-folder</v-icon>
                  <span class="font-weight-600 font-size-16">{{
                    category.name
                  }}</span>
                </div>
              </td>
              <td width="150" class="text-center simple-table-td">
                <v-chip small color="blue-grey" outlined>
                  {{ category?.subcategories?.length }} items
                </v-chip>
              </td>
              <td width="150" class="text-end simple-table-td">
                <v-btn
                  icon
                  small
                  color="green"
                  @click="openAddSubcategoryDialog(category)"
                >
                  <v-icon>mdi-plus-circle</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="cyan"
                  class="ml-1"
                  @click="editCategory(category)"
                >
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="red"
                  class="ml-1"
                  @click="deleteCategoryDialogOpen(category)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>

            <!-- SUBCATEGORY ROWS -->
            <!-- <tr
              v-for="(sub, subIndex) in category?.subcategories"
              :key="'sub-' + sub.id"
              class="alternate-listing-row"
            >
              <td width="50" class="simple-table-td pl-12 font-size-14">
                <v-chip
                  x-small
                  color="blue-grey lighten-3"
                  class="font-weight-500"
                  >#{{ (currentPage - 1) * itemsPerPage + index + 1 }}.{{ subIndex + 1 }}</v-chip
                >
              </td>
              <td width="500" class="simple-table-td pl-12 font-size-14">
                <div class="d-flex align-center">
                  <v-icon color="blue-grey" size="18" class="mr-2"
                    >mdi-subdirectory-arrow-right</v-icon
                  >
                  <span class="font-weight-500">{{ sub.name }}</span>
                </div>
              </td>
              <td width="150" class="simple-table-sd"></td>
              <td width="150" class="text-center simple-table-td">
                <v-btn
                  icon
                  x-small
                  color="cyan"
                  class="mx-1"
                  @click="editSubcategory(category, sub)"
                >
                  <v-icon size="18">mdi-pencil</v-icon>
                </v-btn>
                <v-btn
                  icon
                  x-small
                  color="red"
                  class="mx-1"
                  @click="deleteSubcategoryDialogOpen(category, sub)"
                >
                  <v-icon size="18">mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr> -->
          </template>
          <tr v-else>
            <td colspan="4" class="text-center py-8">
              <img
                :src="$assetURL('media/error/empty.png')"
                class="row-not-found-image"
              />
              <p class="font-size-16 font-weight-500 text-muted">
                No categories found. Click "Add Category" to get started.
              </p>
            </td>
          </tr>
        </tbody>
      </v-simple-table>

      <!-- Pagination -->
      <v-row
        v-if="categories.length > itemsPerPage"
        justify="end"
        class="align-center pagination_btn listing-footer px-2"
      >
        <p class="m-0 listing-footer-count">
          Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to
          {{ Math.min(currentPage * itemsPerPage, categories.length) }}
          of {{ categories.length }} entries
        </p>
        <v-spacer></v-spacer>
        <v-pagination
          v-model="currentPage"
          :length="pageCount"
          total-visible="7"
          color="cyan"
          @input="handlePageChange"
        ></v-pagination>
      </v-row>
    </v-card-text>

    <!-- ================= Add/Edit CATEGORY + SUBCATEGORY Dialog ================= -->
    <Dialog :common-dialog="categoryDialog" :dialog-width="600">
      <template v-slot:title
        >{{ categoryPayload.id ? "Edit" : "Add" }} Category</template
      >
      <template v-slot:body>
        <v-form
          ref="categoryForm"
          v-model.trim="categoryFormValid"
          lazy-validation
          @submit.prevent.stop
        >
          <!-- Category Name -->
          <v-text-field
            placeholder="Category Name *"
            v-model.trim="categoryPayload.name"
            :rules="[
              validateRules.required(categoryPayload.name, 'Category name'),
              validateRules.minLength(
                categoryPayload.name,
                'Category name',
                1
              ),
              validateRules.maxLength(
                categoryPayload.name,
                'Category name',
                100
              ),
            ]"
            maxlength="100"
            class="px-0 mb-4"
            dense
            filled
            solo
            flat
            color="cyan"
            hint="Enter the main category name"
            persistent-hint
          ></v-text-field>

          <!-- SUBCATEGORIES SECTION -->
          <div class="mb-2">
            <!-- Header -->
            <div class="d-flex align-center mb-2">
              <span class="font-weight-600 font-size-14 color-custom-blue">
                Sub-categories
              </span>
              <v-spacer></v-spacer>
            </div>

            <!-- Table -->
            <v-simple-table
              class="subcategory-table elevation-1"
              v-if="categoryPayload.subcategories.length > 0"
            >
              <thead>
                <tr>
                  <th class="text-left" width="40">#</th>
                  <th class="text-left">Subcategory Name</th>
                  <th class="text-center" width="60">Action</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="(sub, index) in categoryPayload.subcategories"
                  :key="'sub-list-' + index"
                  :class="index % 2 === 0 ? 'row-light' : 'row-dark'"
                >
                  <!-- Row Number -->
                  <td>
                    <v-icon small color="cyan darken-2"
                      >mdi-checkbox-blank-circle</v-icon
                    >
                  </td>

                  <!-- Subcategory Name Field -->
                  <td>
                    <v-text-field
                      dense
                      outlined
                      solo
                      flat
                      color="cyan"
                      v-model.trim="sub.name"
                      :placeholder="`Subcategory ${index + 1}`"
                      :rules="[
                        (value) =>
                          validateRules.required(value, 'Subcategory name'),
                        (value) =>
                          validateRules.minLength(value, 'Subcategory name', 1),
                        (value) =>
                          validateRules.maxLength(value, 'Subcategory name', 100),
                      ]"
                      maxlength="100"
                    ></v-text-field>
                  </td>

                  <!-- Action -->
                  <td class="text-center">
                    <v-btn
                      icon
                      small
                      color="red"
                      @click="removeSubcategoryInput(index, sub.id)"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <!-- Empty State -->
            <div v-else class="text-center py-4 mb-4 empty-box">
              <v-icon color="grey lighten-1" size="40" class="mb-2">
                mdi-folder-open-outline
              </v-icon>
              <p class="text--secondary mb-0">
                <em>No sub-categories added yet</em>
              </p>
            </div>

            <!-- Add Button -->
            <v-btn
              small
              color="green lighten-1"
              @click="addSubcategoryInput"
              class="white--text mt-3"
              depressed
              block
            >
              <v-icon left small>mdi-plus</v-icon>
              <span class="font-weight-600">Add Sub-category Field</span>
            </v-btn>

            <div class="text-center mt-2">
              <small class="text--secondary">
                Click to add multiple sub-categories for this category
              </small>
            </div>
          </div>
        </v-form>
      </template>
      <template v-slot:action>
        <v-btn @click="closeCategoryDialog" depressed class="mr-2"
          >Cancel</v-btn
        >
        <v-btn
          color="cyan white--text"
          @click="saveCategoryWithSubcategories"
          :loading="categoryLoading"
          depressed
          >Save Category</v-btn
        >
      </template>
    </Dialog>

    <!-- ADD/EDIT SUBCATEGORY Dialog -->
    <Dialog :common-dialog="subcategoryDialog" :dialog-width="500">
      <template v-slot:title>
        <div class="d-flex align-center">
          <v-icon color="cyan" class="mr-2">
            {{ subcategoryPayload.id ? "mdi-pencil" : "mdi-plus" }}
          </v-icon>
          {{ subcategoryPayload.id ? "Edit" : "Add" }} Sub-category
        </div>
      </template>
      <template v-slot:body>
        <v-form
          ref="subcategoryForm"
          v-model.trim="subcategoryFormValid"
          lazy-validation
          @submit.prevent.stop
        >
          <v-text-field
            label="Subcategory Name *"
            v-model.trim="subcategoryPayload.name"
            :rules="[
              (value) => validateRules.required(value, 'Subcategory name'),
              (value) => validateRules.minLength(value, 'Subcategory name', 1),
              (value) => validateRules.maxLength(value, 'Subcategory name', 100),
            ]"
            maxlength="100"
            class="px-0"
            dense
            filled
            solo
            flat
            color="cyan"
            placeholder="Enter Sub-category Name"
            hint="Enter a descriptive name for the sub-category"
            persistent-hint
          ></v-text-field>

          <!-- Category Info (when adding new subcategory) -->
          <div
            v-if="!subcategoryPayload.id && subcategoryPayload.category_id"
            class="mt-3 pa-3"
            style="
              background-color: #e8f4f8;
              border-radius: 4px;
              border-left: 3px solid #00bcd4;
            "
          >
            <div class="d-flex align-center">
              <v-icon small color="cyan" class="mr-2">mdi-information</v-icon>
              <span class="font-weight-500 font-size-14">
                This sub-category will be added to:
                <span class="cyan--text text--darken-2">
                  {{ getCategoryName(subcategoryPayload.category_id) }}
                </span>
              </span>
            </div>
          </div>
        </v-form>
      </template>
      <template v-slot:action>
        <v-btn @click="closeSubcategoryDialog" depressed class="mr-2"
          >Cancel</v-btn
        >
        <v-btn
          color="cyan white--text"
          @click="saveSubcategory"
          :loading="subcategoryLoading"
          depressed
          >{{ subcategoryPayload.id ? "Update" : "Save" }}</v-btn
        >
      </template>
    </Dialog>

    <!-- DELETE CATEGORY DIALOG -->
    <DeleteDialog :delete-dialog="deleteCategoryDialog">
      <template v-slot:title>Delete Category</template>
      <template v-slot:text>
        Are you sure you want to delete category "{{
          deleteCategoryData?.name
        }}"? <br />
        <small>This will also delete all associated sub-categories.</small>
      </template>
      <template v-slot:action>
        <v-btn @click="deleteCategoryDialog = false">Cancel</v-btn>
        <v-btn
          color="red"
          @click="confirmDeleteCategory"
          :loading="deleteCategoryLoading"
          >Delete</v-btn
        >
      </template>
    </DeleteDialog>

    <!-- DELETE SUBCATEGORY DIALOG -->
    <DeleteDialog :delete-dialog="deleteSubcategoryDialog">
      <template v-slot:title>Delete Sub-category</template>
      <template v-slot:text>
        Are you sure you want to delete sub-category "{{
          deleteSubcategoryData?.name
        }}"?
      </template>
      <template v-slot:action>
        <v-btn @click="deleteSubcategoryDialog = false">Cancel</v-btn>
        <v-btn
          color="red"
          @click="confirmDeleteSubcategory"
          :loading="deleteSubcategoryLoading"
          >Delete</v-btn
        >
      </template>
    </DeleteDialog>
  </v-card>
</template>

<script>
import Dialog from "@/view/pages/partials/Dialog.vue";
import DeleteDialog from "@/view/pages/partials/Delete.vue";
import { GET, POST, PATCH, DELETE } from "@/core/services/store/request.module";
import { ErrorEventBus, SuccessEventBus } from "@/core/lib/message.lib";
import { cloneDeep } from "lodash";
import ValidationMixin from "@/core/plugins/validation-mixin";

export default {
  mixins: [ValidationMixin],
  components: { Dialog, DeleteDialog },
  data() {
    return {
      categories: [],
      categoryPayload: { id: null, name: "", subcategories: [] },
      deletedIds: [],
      categoryDialog: false,
      categoryFormValid: true,
      categoryLoading: false,

      subcategoryPayload: { id: null, name: "", category_id: null },
      subcategoryDialog: false,
      subcategoryFormValid: true,
      subcategoryLoading: false,

      deleteCategoryDialog: false,
      deleteCategoryData: null,
      deleteCategoryLoading: false,

      deleteSubcategoryDialog: false,
      deleteSubcategoryData: null,
      deleteSubcategoryLoading: false,

      // Pagination
      itemsPerPage: 10,
      currentPage: 1,
    };
  },
  methods: {
    // getPermission(permission) {
    //   // Ensure this method exists in parent component or add fallback
    //   return this.$store && this.$store.getters.getPermissions
    //     ? this.$store.getters.getPermissions.includes(permission)
    //     : true;
    // },

    getCategories() {
      this.$store
        .dispatch(GET, { url: "setting/quotation/categories" })
        .then(({ data }) => {
          console.log(data, "setting/quotation/categories");
          this.categories = data;
          // Ensure data exists and is an array before mapping
          /* if (data && data.data && Array.isArray(data.data)) {
            this.categories = data.data.map((cat) => ({
              ...cat,
              subcategories: cat.subcategories || [],
            }));
          } else {
            this.categories = [];
          } */
          // Reset to first page when data changes
          this.currentPage = 1;
        })
        .catch((err) => console.error(err));
    },

    // ===== CATEGORY DIALOG METHODS =====
    openAddCategoryDialog() {
      this.categoryPayload = { id: null, name: "", subcategories: [] };
      this.categoryDialog = true;
    },

    addSubcategoryInput() {
      let subcategory =
        this.categoryPayload && this.categoryPayload.subcategories
          ? cloneDeep(this.categoryPayload.subcategories)
          : [];
      subcategory.push({ name: "" });
      this.categoryPayload.subcategories = subcategory;
    },

    removeSubcategoryInput(index, id) {
      let subcategory = cloneDeep(this.categoryPayload.subcategories);
      subcategory.splice(index, 1);
      this.categoryPayload.subcategories = subcategory;
      if (id) {
        let ids =
          this.categoryPayload && this.categoryPayload.deletedIds
            ? cloneDeep(this.categoryPayload.deletedIds)
            : [];
        ids.push(id);
        this.categoryPayload.deletedIds = ids;
      }
    },

    closeCategoryDialog() {
      this.deletedIds = [];
      this.categoryDialog = false;
      this.categoryPayload = { id: null, name: "", subcategories: [] };
      if (this.$refs.categoryForm) this.$refs.categoryForm.reset();
    },

    // ===== SUBCATEGORY DIALOG METHODS =====
    openAddSubcategoryDialog(category) {
      this.subcategoryPayload = {
        id: null,
        name: "",
        category_id: category.id,
      };
      this.subcategoryDialog = true;
    },

    closeSubcategoryDialog() {
      this.subcategoryDialog = false;
      this.subcategoryPayload = { id: null, name: "", category_id: null };
      if (this.$refs.subcategoryForm) this.$refs.subcategoryForm.reset();
    },

    saveSubcategory() {
      if (!this.$refs.subcategoryForm.validate()) return;

      this.subcategoryLoading = true;
      const method = this.subcategoryPayload.id ? PATCH : POST;
      const url = this.subcategoryPayload.id
        ? `setting/quotation/subcategories/${this.subcategoryPayload.id}`
        : "setting/quotation/subcategories";
      const isUpdate = !!this.subcategoryPayload.id;

      this.$store
        .dispatch(method, { url, data: this.subcategoryPayload })
        .then(() => {
          this.getCategories();
          this.closeSubcategoryDialog();
          SuccessEventBus.$emit(
            "update:success",
            isUpdate
              ? "Subcategory updated successfully!"
              : "Subcategory created successfully!"
          );
        })
        .catch((err) => {
          console.error(err);
          ErrorEventBus.$emit(
            "update:error",
            err?.response?.data?.message || "Error saving sub-category"
          );
        })
        .finally(() => (this.subcategoryLoading = false));
    },

    saveCategoryWithSubcategories() {
      if (!this.$refs.categoryForm.validate()) return;

      this.categoryLoading = true;
      const method = this.categoryPayload.id ? PATCH : POST;
      const url = this.categoryPayload.id
        ? `setting/quotation/categories/${this.categoryPayload.id}`
        : "setting/quotation/categories";
      // First save the category
      this.$store
        .dispatch(method, { url, data: this.categoryPayload })
        .then(() => {
          this.getCategories();
          this.closeCategoryDialog();
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => (this.categoryLoading = false));
    },

    editCategory(category) {
      this.categoryPayload = {
        id: category.id,
        name: category.name,
        subcategories: cloneDeep(category.subcategories || []),
      };
      this.deletedIds = [];
      this.categoryDialog = true;
    },

    editSubcategory(category, sub) {
      this.subcategoryPayload = {
        id: sub.id,
        name: sub.name,
        category_id: category.id,
      };
      this.subcategoryDialog = true;
    },

    getCategoryName(categoryId) {
      const category = this.categories.find((cat) => cat.id === categoryId);
      return category ? category.name : "";
    },

    // ===== DELETE METHODS =====
    deleteCategoryDialogOpen(category) {
      this.deleteCategoryData = category;
      this.deleteCategoryDialog = true;
    },

    confirmDeleteCategory() {
      this.deleteCategoryLoading = true;
      this.$store
        .dispatch(DELETE, {
          url: `setting/quotation/categories/${this.deleteCategoryData.id}`,
        })
        .then(() => {
          this.deleteCategoryDialog = false;
          SuccessEventBus.$emit(
            "update:success",
            "Category deleted successfully!"
          );
          this.getCategories();
        })
        .catch((err) => {
          console.error(err);
          ErrorEventBus.$emit(
            "update:error",
            err?.response?.data?.message || "Error deleting category"
          );
        })
        .finally(() => (this.deleteCategoryLoading = false));
    },

    deleteSubcategoryDialogOpen(category, sub) {
      this.deleteSubcategoryData = sub;
      this.deleteSubcategoryDialog = true;
    },

    confirmDeleteSubcategory() {
      this.deleteSubcategoryLoading = true;
      this.$store
        .dispatch(DELETE, {
          url: `setting/quotation/subcategories/${this.deleteSubcategoryData.id}`,
        })
        .then(() => {
          this.deleteSubcategoryDialog = false;
          SuccessEventBus.$emit(
            "update:success",
            "Sub-category deleted successfully!"
          );
          this.getCategories();
        })
        .catch((err) => {
          console.error(err);
          ErrorEventBus.$emit(
            "update:error",
            err?.response?.data?.message || "Error deleting sub-category"
          );
        })
        .finally(() => (this.deleteSubcategoryLoading = false));
    },

    // Pagination
    handlePageChange(page) {
      this.currentPage = page;
    },
  },
  mounted() {
    this.getCategories();
  },
  computed: {
    pageCount() {
      return Math.ceil(this.categories.length / this.itemsPerPage);
    },
    paginatedCategories() {
      // Sort by ID descending (newest first)
      const sorted = [...this.categories].sort((a, b) => b.id - a.id);
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return sorted.slice(start, end);
    },
  },
};
</script>

<style scoped>
/* Subcategory input styling */
.subcategory-input >>> .v-input__slot {
  min-height: 38px !important;
}

.subcategory-input >>> .v-text-field__details {
  margin-bottom: 0 !important;
  padding-top: 2px !important;
}

/* Dialog action buttons */
::v-deep .v-dialog__actions {
  padding: 16px 24px !important;
  border-top: 1px solid #e0e0e0;
}

/* Chip styling for better visibility */
.v-chip--small {
  height: 24px !important;
}

/* Table styling */
.simple-table {
  border-radius: 4px;
  overflow: hidden;
}

.simple-table >>> th {
  background-color: #f5f5f5 !important;
}

/* Empty state image */
.row-not-found-image {
  max-width: 200px;
  opacity: 0.7;
  margin-bottom: 16px;
}
</style>
